import React, { useCallback, useEffect, useState } from "react";
import {
  crmApi,
  extractCrmError,
  getCustomerPaymentMethodLabel,
  getCustomerPaymentStatusLabel,
  MANUAL_PAYMENT_METHOD_OPTIONS,
} from "@/lib/crmApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Loader2, MessageCircle, QrCode, Receipt, Wallet } from "lucide-react";
import { toast } from "sonner";
import { formatCrmDateTime } from "@/utils/crmDateTime";

const PAYMENT_STATUS_STYLES = {
  pending: "border-amber-200 bg-amber-50 text-amber-700",
  paid: "border-emerald-200 bg-emerald-50 text-emerald-700",
  failed: "border-red-200 bg-red-50 text-red-700",
  cancelled: "border-slate-200 bg-slate-50 text-slate-600",
  refunded: "border-violet-200 bg-violet-50 text-violet-700",
};

function formatMoneyKzt(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
  return `${Number(value).toLocaleString("ru-RU")} ₸`;
}

function computeKaspiSuggestedAmount(mode, orderTotal, remaining) {
  if (remaining <= 0) return 0;
  if (mode === "full") return remaining;
  const half = Math.max(1, Math.round(Number(orderTotal || 0) * 0.5));
  return Math.min(half, remaining);
}

export default function CustomerOrderPaymentsPanel({
  orderId,
  token,
  canEdit = false,
  compact = false,
  onOrderUpdated,
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [kaspiHealth, setKaspiHealth] = useState(null);
  const [whatsappSendEnabled, setWhatsappSendEnabled] = useState(false);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [comment, setComment] = useState("");
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [invoiceDialogMode, setInvoiceDialogMode] = useState("prepayment");
  const [invoiceDialogPartialAmount, setInvoiceDialogPartialAmount] = useState("");
  const [invoiceDialogWhatsapp, setInvoiceDialogWhatsapp] = useState(false);

  const loadPayments = useCallback(async () => {
    if (!token || !orderId) return;
    setLoading(true);
    setError(null);
    try {
      const [payload, kaspi, health] = await Promise.all([
        crmApi.listCustomerOrderPayments(token, orderId),
        crmApi.getKaspiHealth(token).catch(() => null),
        crmApi.health().catch(() => null),
      ]);
      setData(payload);
      setKaspiHealth(kaspi);
      setWhatsappSendEnabled(Boolean(health?.whatsapp_send_enabled));
    } catch (err) {
      setError(extractCrmError(err, "Не удалось загрузить оплаты"));
    } finally {
      setLoading(false);
    }
  }, [token, orderId]);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  useEffect(() => {
    setAmount("");
    setInvoiceDialogMode("prepayment");
    setInvoiceDialogPartialAmount("");
    setInvoiceDialogOpen(false);
  }, [orderId]);

  const orderTotalKzt = data?.order_total_kzt ?? 0;
  const remainingAmount = data?.remaining_amount ?? 0;
  const defaultPartialAmount = computeKaspiSuggestedAmount(
    "prepayment",
    orderTotalKzt,
    remainingAmount,
  );
  const parsedPartialAmount = Math.round(Number(invoiceDialogPartialAmount));
  const invoiceDialogAmount = invoiceDialogMode === "full"
    ? remainingAmount
    : (parsedPartialAmount > 0 ? Math.min(parsedPartialAmount, remainingAmount) : 0);
  const invoiceDialogBalance = Math.max(0, remainingAmount - invoiceDialogAmount);
  const isPartialAmountValid = invoiceDialogMode === "full"
    || (parsedPartialAmount > 0 && parsedPartialAmount <= remainingAmount);

  const openInvoiceDialog = (sendWhatsapp) => {
    setInvoiceDialogWhatsapp(sendWhatsapp);
    setInvoiceDialogMode("prepayment");
    setInvoiceDialogPartialAmount(String(defaultPartialAmount || ""));
    setInvoiceDialogOpen(true);
  };

  const confirmInvoiceDialog = async () => {
    if (!isPartialAmountValid) {
      if (invoiceDialogMode === "prepayment" && parsedPartialAmount > remainingAmount) {
        toast.error("Сумма не может превышать остаток по заказу");
      } else {
        toast.error("Укажите сумму частичной оплаты");
      }
      return;
    }
    setInvoiceDialogOpen(false);
    await handleKaspiInvoice({
      sendWhatsapp: invoiceDialogWhatsapp,
      invoiceAmount: invoiceDialogAmount,
    });
  };

  const refreshOrder = useCallback(async () => {
    if (!onOrderUpdated || !token || !orderId) return;
    try {
      const order = await crmApi.getCustomerOrder(token, orderId);
      onOrderUpdated(order);
    } catch {
      // ignore — payments list already refreshed
    }
  }, [onOrderUpdated, token, orderId]);

  const handleCreate = async (event) => {
    event.preventDefault();
    if (!canEdit || !token) return;
    const parsedAmount = Number(amount);
    if (!parsedAmount || parsedAmount <= 0) {
      toast.error("Укажите сумму оплаты");
      return;
    }
    setSaving(true);
    try {
      await crmApi.createCustomerOrderPayment(token, orderId, {
        amount: Math.round(parsedAmount),
        payment_method: method,
        status: "paid",
        comment: comment.trim() || undefined,
      });
      setAmount("");
      setComment("");
      await loadPayments();
      await refreshOrder();
      toast.success("Оплата записана");
    } catch (err) {
      toast.error(extractCrmError(err, "Не удалось записать оплату"));
    } finally {
      setSaving(false);
    }
  };

  const handleRefund = async (paymentId) => {
    if (!canEdit || !token) return;
    setActionId(paymentId);
    try {
      const payment = (data?.items || []).find((item) => item.id === paymentId);
      const isKaspi = payment?.payment_method === "kaspi_qr" || payment?.payment_method === "kaspi_invoice";
      if (isKaspi) {
        await crmApi.refundKaspiPayment(token, paymentId);
      } else {
        await crmApi.refundCustomerPayment(token, paymentId);
      }
      await loadPayments();
      await refreshOrder();
      toast.success("Возврат оформлен");
    } catch (err) {
      toast.error(extractCrmError(err, "Не удалось оформить возврат"));
    } finally {
      setActionId(null);
    }
  };

  const handleKaspiInvoice = async ({ sendWhatsapp = false, invoiceAmount: explicitAmount } = {}) => {
    if (!canEdit || !token || remainingAmount <= 0) return;
    setSaving(true);
    try {
      const invoiceAmount = Math.round(Number(explicitAmount));
      if (!invoiceAmount || invoiceAmount <= 0) {
        toast.error("Укажите сумму счёта");
        return;
      }
      const payment = await crmApi.createKaspiInvoice(token, orderId, {
        amount: invoiceAmount,
        comment: comment.trim() || undefined,
        send_whatsapp: sendWhatsapp,
      });
      setComment("");
      setInvoiceDialogMode("prepayment");
      await loadPayments();
      await refreshOrder();
      if (sendWhatsapp && payment?.id) {
        toast.success("Kaspi-счёт выставлен и отправлен в WhatsApp");
        return;
      }
      toast.success("Kaspi-счёт выставлен");
    } catch (err) {
      toast.error(extractCrmError(err, "Не удалось выставить Kaspi-счёт"));
    } finally {
      setSaving(false);
    }
  };

  const handleKaspiQr = async ({ sendWhatsapp = false } = {}) => {
    if (!canEdit || !token || remainingAmount <= 0) return;
    setSaving(true);
    try {
      const parsedAmount = Number(amount);
      const qrAmount = parsedAmount > 0
        ? Math.round(parsedAmount)
        : remainingAmount;
      if (!qrAmount || qrAmount <= 0) {
        toast.error("Укажите сумму оплаты");
        return;
      }
      const payment = await crmApi.createKaspiQr(token, orderId, {
        amount: qrAmount,
        comment: comment.trim() || undefined,
        send_whatsapp: sendWhatsapp,
      });
      setComment("");
      setAmount("");
      await loadPayments();
      await refreshOrder();
      if (payment?.qr_token) {
        window.open(payment.qr_token, "_blank", "noopener,noreferrer");
      }
      if (sendWhatsapp) {
        toast.success("Kaspi QR создан и отправлен в WhatsApp");
        return;
      }
      toast.success("Kaspi QR создан");
    } catch (err) {
      toast.error(extractCrmError(err, "Не удалось создать Kaspi QR"));
    } finally {
      setSaving(false);
    }
  };

  const handleSendKaspiWhatsApp = async (paymentId) => {
    if (!canEdit || !token) return;
    setActionId(paymentId);
    try {
      const result = await crmApi.sendKaspiPaymentWhatsApp(token, paymentId);
      if (!result?.success) {
        if (result?.error === "send_disabled") {
          toast.error("Отправка WhatsApp отключена в настройках CRM");
        } else if (result?.error === "missing_chat_id") {
          toast.error("У клиента нет WhatsApp chat_id — отправка невозможна");
        } else {
          toast.error(result?.error || "Не удалось отправить сообщение в WhatsApp");
        }
        return;
      }
      toast.success("Счёт отправлен клиенту в WhatsApp");
    } catch (err) {
      toast.error(extractCrmError(err, "Не удалось отправить в WhatsApp"));
    } finally {
      setActionId(null);
    }
  };

  const handleCancel = async (paymentId) => {
    if (!canEdit || !token) return;
    setActionId(paymentId);
    try {
      const payment = (data?.items || []).find((item) => item.id === paymentId);
      const isKaspiPending =
        payment?.status === "pending"
        && (payment?.payment_method === "kaspi_qr" || payment?.payment_method === "kaspi_invoice");
      if (isKaspiPending) {
        await crmApi.cancelKaspiPayment(token, paymentId);
      } else {
        await crmApi.cancelCustomerPayment(token, paymentId);
      }
      await loadPayments();
      await refreshOrder();
      toast.success("Оплата отменена");
    } catch (err) {
      toast.error(extractCrmError(err, "Не удалось отменить оплату"));
    } finally {
      setActionId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Загрузка оплат...
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-700">
        {error}
      </div>
    );
  }

  const items = data?.items || [];
  const remaining = data?.remaining_amount ?? 0;
  const kaspiReady = Boolean(kaspiHealth?.enabled && kaspiHealth?.session?.configured);
  const kaspiSessionActive = kaspiHealth?.session?.active !== false;
  const canSendKaspiWhatsApp = whatsappSendEnabled && kaspiReady && kaspiSessionActive;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Wallet className="h-4 w-4 text-slate-500" />
          <h4 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">
            Оплаты
          </h4>
        </div>
        {data?.is_fully_paid ? (
          <Badge className="border-emerald-200 bg-emerald-50 text-emerald-700" variant="outline">
            Полностью оплачен
          </Badge>
        ) : null}
      </div>

      <div className={`grid gap-3 ${compact ? "md:grid-cols-3" : "md:grid-cols-4"}`}>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">К оплате</p>
          <p className="mt-2 text-xl font-semibold text-slate-950">{formatMoneyKzt(data?.order_total_kzt)}</p>
        </div>
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-4 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-emerald-700">Оплачено</p>
          <p className="mt-2 text-xl font-semibold text-emerald-900">{formatMoneyKzt(data?.paid_amount)}</p>
        </div>
        <div className="rounded-2xl border border-amber-200 bg-amber-50/40 p-4 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-amber-700">Остаток</p>
          <p className="mt-2 text-xl font-semibold text-amber-900">{formatMoneyKzt(remaining)}</p>
        </div>
      </div>

      {canEdit && remaining > 0 ? (
        <Card className="border-slate-200 bg-white shadow-sm">
          <CardContent className="space-y-4 p-4">
            <p className="text-sm font-medium text-slate-950">Записать оплату</p>
            <form className="grid gap-4 md:grid-cols-4" onSubmit={handleCreate}>
              <div className="space-y-2">
                <Label htmlFor={`payment-amount-${orderId}`}>Сумма, ₸</Label>
                <Input
                  id={`payment-amount-${orderId}`}
                  type="number"
                  min="1"
                  step="1"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder={String(remaining || "")}
                />
              </div>
              <div className="space-y-2">
                <Label>Способ</Label>
                <Select value={method} onValueChange={setMethod}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MANUAL_PAYMENT_METHOD_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor={`payment-comment-${orderId}`}>Комментарий</Label>
                <Textarea
                  id={`payment-comment-${orderId}`}
                  rows={2}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Необязательно"
                />
              </div>
              <div className="md:col-span-4 flex flex-wrap gap-2">
                <Button type="submit" disabled={saving}>
                  {saving ? "Сохранение..." : "Добавить оплату"}
                </Button>
              </div>
              {kaspiReady ? (
                <div className="md:col-span-4 space-y-3 rounded-xl border border-slate-100 bg-slate-50/80 p-3">
                  <p className="text-sm font-medium text-slate-700">Оплата через Kaspi</p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={saving || !kaspiSessionActive}
                      onClick={() => openInvoiceDialog(false)}
                    >
                      <Receipt className="mr-2 h-4 w-4" />
                      Kaspi счёт
                    </Button>
                    {canSendKaspiWhatsApp ? (
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={saving || !kaspiSessionActive}
                        onClick={() => openInvoiceDialog(true)}
                      >
                        <MessageCircle className="mr-2 h-4 w-4" />
                        Счёт → WhatsApp
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={saving || !kaspiSessionActive}
                      onClick={() => handleKaspiQr()}
                    >
                      <QrCode className="mr-2 h-4 w-4" />
                      Kaspi QR
                    </Button>
                    {canSendKaspiWhatsApp ? (
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={saving || !kaspiSessionActive}
                        onClick={() => handleKaspiQr({ sendWhatsapp: true })}
                      >
                        <MessageCircle className="mr-2 h-4 w-4" />
                        QR → WhatsApp
                      </Button>
                    ) : null}
                  </div>
                </div>
              ) : null}
              {kaspiHealth?.enabled && !kaspiReady ? (
                <p className="md:col-span-4 text-xs text-amber-700">
                  Kaspi включён, но сессия кассира не настроена. Авторизуйтесь через админку.
                </p>
              ) : null}
              {kaspiReady && !kaspiSessionActive ? (
                <p className="md:col-span-4 text-xs text-red-700">
                  Сессия Kaspi недоступна — нужна повторная авторизация кассира.
                </p>
              ) : null}
            </form>
          </CardContent>
        </Card>
      ) : null}

      {items.length > 0 ? (
        <Card className="border-slate-200 bg-white shadow-sm">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead>Дата</TableHead>
                  <TableHead>Способ</TableHead>
                  <TableHead className="text-right">Сумма</TableHead>
                  <TableHead>Статус</TableHead>
                  <TableHead>Комментарий</TableHead>
                  {canEdit ? <TableHead className="w-[160px]" /> : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((payment) => (
                  <TableRow key={payment.id}>
                    <TableCell>{formatCrmDateTime(payment.paid_at || payment.created_at)}</TableCell>
                    <TableCell>{getCustomerPaymentMethodLabel(payment.payment_method)}</TableCell>
                    <TableCell className="text-right font-medium">{formatMoneyKzt(payment.amount)}</TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={PAYMENT_STATUS_STYLES[payment.status] || "border-slate-200 bg-slate-50 text-slate-700"}
                      >
                        {getCustomerPaymentStatusLabel(payment.status)}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-[220px] truncate">
                      <div className="space-y-1">
                        <div>{payment.comment || "—"}</div>
                        {payment.qr_token && payment.status === "pending" ? (
                          <a
                            href={payment.qr_token}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline"
                          >
                            Открыть QR
                          </a>
                        ) : null}
                        {payment.receipt_url && payment.status === "paid" ? (
                          <a
                            href={payment.receipt_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block text-xs text-blue-600 hover:underline"
                          >
                            Чек
                          </a>
                        ) : null}
                      </div>
                    </TableCell>
                    {canEdit ? (
                      <TableCell>
                        <div className="flex flex-wrap gap-2">
                          {payment.status === "paid" ? (
                            <>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                disabled={actionId === payment.id}
                                onClick={() => handleRefund(payment.id)}
                              >
                                Возврат
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                disabled={actionId === payment.id}
                                onClick={() => handleCancel(payment.id)}
                              >
                                Отмена
                              </Button>
                            </>
                          ) : payment.status === "pending" ? (
                            <>
                              {(payment.payment_method === "kaspi_invoice" || payment.payment_method === "kaspi_qr") ? (
                                <>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="secondary"
                                    disabled={actionId === payment.id}
                                    onClick={async () => {
                                      setActionId(payment.id);
                                      try {
                                        await crmApi.syncKaspiPayment(token, payment.id);
                                        await loadPayments();
                                        await refreshOrder();
                                        toast.success("Статус Kaspi обновлён");
                                      } catch (err) {
                                        toast.error(extractCrmError(err, "Не удалось синхронизировать Kaspi"));
                                      } finally {
                                        setActionId(null);
                                      }
                                    }}
                                  >
                                    Kaspi ↻
                                  </Button>
                                  {canSendKaspiWhatsApp ? (
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      disabled={actionId === payment.id}
                                      onClick={() => handleSendKaspiWhatsApp(payment.id)}
                                    >
                                      <MessageCircle className="mr-1 h-3.5 w-3.5" />
                                      WhatsApp
                                    </Button>
                                  ) : null}
                                </>
                              ) : null}
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                disabled={actionId === payment.id}
                                onClick={() => handleCancel(payment.id)}
                              >
                                Отмена
                              </Button>
                            </>
                          ) : null}
                        </div>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-5 text-sm text-slate-500">
          Оплат по заказу пока нет.
        </div>
      )}

      <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {invoiceDialogWhatsapp ? "Отправить Kaspi-счёт в WhatsApp" : "Выставить Kaspi-счёт"}
            </DialogTitle>
            <DialogDescription>
              Выберите тип оплаты перед выставлением счёта клиенту.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <RadioGroup
              value={invoiceDialogMode}
              onValueChange={setInvoiceDialogMode}
              className="gap-3"
            >
              <label
                htmlFor="invoice-prepayment"
                className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 p-4 has-[:checked]:border-slate-400 has-[:checked]:bg-slate-50"
              >
                <RadioGroupItem value="prepayment" id="invoice-prepayment" className="mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-slate-950">Частичная оплата</p>
                  <p className="text-xs text-slate-500">
                    Укажите сумму предоплаты — остаток клиент оплатит при получении
                  </p>
                </div>
              </label>
              <label
                htmlFor="invoice-full"
                className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 p-4 has-[:checked]:border-slate-400 has-[:checked]:bg-slate-50"
              >
                <RadioGroupItem value="full" id="invoice-full" className="mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-slate-950">Полная оплата</p>
                  <p className="text-xs text-slate-500">
                    К оплате сейчас: {formatMoneyKzt(remaining)}
                  </p>
                </div>
              </label>
            </RadioGroup>

            {invoiceDialogMode === "prepayment" ? (
              <div className="space-y-2">
                <Label htmlFor={`invoice-partial-amount-${orderId}`}>Сумма частичной оплаты, ₸</Label>
                <Input
                  id={`invoice-partial-amount-${orderId}`}
                  type="number"
                  min="1"
                  max={remaining}
                  step="1"
                  value={invoiceDialogPartialAmount}
                  onChange={(e) => setInvoiceDialogPartialAmount(e.target.value)}
                  placeholder={String(defaultPartialAmount || "")}
                />
                <p className="text-xs text-slate-500">
                  Доступно до {formatMoneyKzt(remaining)}
                  {defaultPartialAmount > 0 ? ` · по умолчанию 50%: ${formatMoneyKzt(defaultPartialAmount)}` : ""}
                </p>
              </div>
            ) : null}

            <div className="rounded-xl border border-slate-100 bg-slate-50/80 p-4 text-sm text-slate-700">
              <div className="flex items-center justify-between gap-3">
                <span>Сумма счёта</span>
                <span className="text-lg font-semibold text-slate-950">
                  {formatMoneyKzt(invoiceDialogAmount)}
                </span>
              </div>
              <div className="mt-2 flex items-center justify-between gap-3 text-xs text-slate-500">
                <span>Сумма заказа</span>
                <span>{formatMoneyKzt(orderTotalKzt)}</span>
              </div>
              {invoiceDialogMode === "prepayment" && invoiceDialogBalance > 0 ? (
                <div className="mt-2 flex items-center justify-between gap-3 text-xs text-amber-700">
                  <span>Остаток при получении</span>
                  <span className="font-medium">{formatMoneyKzt(invoiceDialogBalance)}</span>
                </div>
              ) : null}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={saving}
              onClick={() => setInvoiceDialogOpen(false)}
            >
              Отмена
            </Button>
            <Button
              type="button"
              disabled={saving || !isPartialAmountValid}
              onClick={confirmInvoiceDialog}
            >
              {saving
                ? "Отправка..."
                : invoiceDialogWhatsapp
                  ? "Выставить и отправить"
                  : "Выставить счёт"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
